import { type ComponentType, type ReactNode } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CreditCard, LayoutDashboard, LogOut, Moon, Search, Sun, Users } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { Badge, Button, type BadgeProps } from './ui';
import { ProfileSetupModal } from './ProfileSetupModal';
import { cn } from '../lib/cn';
import { useTheme } from '../lib/theme';

export function roleBadgeVariant(role: string): BadgeProps['variant'] {
  if (role === 'super_admin') return 'purple';
  if (role === 'org_admin') return 'primary';
  return 'default';
}

interface NavItem {
  to: string;
  icon: ComponentType<{ className?: string }>;
  key: string;
  end?: boolean;
  adminOnly?: boolean;
}
const NAV: NavItem[] = [
  { to: '/dashboard', icon: LayoutDashboard, key: 'nav.dashboard', end: true },
  { to: '/tracking', icon: Search, key: 'nav.tracking' },
  { to: '/team', icon: Users, key: 'nav.team', adminOnly: true },
  { to: '/billing', icon: CreditCard, key: 'nav.billing', adminOnly: true },
];

/** App-Shell (F1): ruhige Linear-/Stripe-Navigation, Sprache, Theme, rollenabhängig. */
export function Layout({ children }: { children: ReactNode }): JSX.Element {
  const { t, i18n } = useTranslation();
  const { user, logout, refreshUser } = useAuth();
  const { dark, toggle } = useTheme();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'org_admin' || user?.role === 'super_admin';
  // Erstlogin ohne hinterlegten Namen → Profil-Popup (Vor-/Nachname für den Erfasser-Badge).
  const needsProfile = !!user && (!user.firstName || !user.lastName);

  async function onLogout(): Promise<void> {
    await logout();
    navigate('/login');
  }

  return (
    <div className="relative min-h-screen">
      {/* Durchgehender Container-Wand-Hintergrund (Markenklammer fürs ganze Portal). */}
      <div
        className="fixed inset-0 -z-10 bg-cover bg-center"
        style={{ backgroundImage: "url('/hero-containers.webp')" }}
        aria-hidden
      />
      <div className="fixed inset-0 -z-10 bg-background/60 dark:bg-background/55" aria-hidden />
      <header className="sticky top-0 z-10 border-b bg-card/80 backdrop-blur">
        <div className="flex h-14 items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-6">
            <Link to="/dashboard" className="flex items-center">
              <img src="/brand-fclai-transparent.webp" alt="fclAI" className="h-9 w-auto sm:h-10" />
            </Link>
            <nav className="flex items-center gap-1">
              {NAV.filter((n) => !n.adminOnly || isAdmin).map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-accent text-accent-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                    )
                  }
                >
                  <n.icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{t(n.key)}</span>
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-2">
            <select
              aria-label={t('nav.language')}
              value={i18n.language}
              onChange={(e) => void i18n.changeLanguage(e.target.value)}
              className="h-8 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="de">DE</option>
              <option value="en">EN</option>
            </select>
            <Button variant="ghost" size="icon" onClick={toggle} aria-label="Theme" title="Theme">
              {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            {user && (
              <>
                <span className="hidden text-sm text-muted-foreground lg:inline">{user.email}</span>
                <Badge variant={roleBadgeVariant(user.role)}>{t(`role.${user.role}`)}</Badge>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => void onLogout()}
                  aria-label={t('auth.logout')}
                  title={t('auth.logout')}
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        </div>
      </header>
      <main className="w-full px-4 py-8 sm:px-6 lg:px-8">{children}</main>
      <ProfileSetupModal open={needsProfile} onSaved={refreshUser} />
    </div>
  );
}
